package fr.frux.feature.map

import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import fr.frux.core.designsystem.components.harvestPeriodLabel
import fr.frux.core.designsystem.components.untilEndOfLabel
import fr.frux.core.designsystem.icons.SpeciesFamily
import fr.frux.core.designsystem.icons.legendColor
import fr.frux.core.designsystem.icons.speciesFamilyKey
import fr.frux.core.designsystem.icons.speciesGlyphKey
import fr.frux.core.designsystem.theme.FruxExtendedColors
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.color
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.core.maps.MapMarker
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_legend_family_autre
import fr.frux.feature.map.resources.map_legend_family_coque
import fr.frux.feature.map.resources.map_legend_family_dore
import fr.frux.feature.map.resources.map_legend_family_pomme_poire
import fr.frux.feature.map.resources.map_legend_family_prune_baies
import fr.frux.feature.map.resources.map_legend_family_rouge
import fr.frux.shared.domain.model.HarvestPart
import fr.frux.shared.domain.model.HarvestWindow
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.presentation.resolveTreeStatus
import org.jetbrains.compose.resources.stringResource
import kotlin.time.ExperimentalTime
import kotlin.time.Instant
import fr.frux.shared.domain.model.TreeStatus as DomainTreeStatus

/**
 * Neutralise depuis #196 : la couleur du marqueur (statut resolu, cf. [resolvedStatus]) porte
 * desormais a elle seule la distinction de statut. L'alpha ne fait plus qu'attenuer legerement
 * les deux statuts non actionnables (cueilli / indisponible) ; les autres restent a pleine
 * opacite.
 */
fun DomainTreeStatus.markerAlpha(): Float =
    when (this) {
        DomainTreeStatus.RIPE_READY -> 1f
        DomainTreeStatus.FLOWERING -> 1f
        DomainTreeStatus.PICKED_CLEAN -> 0.7f
        DomainTreeStatus.UNAVAILABLE -> 0.7f
        DomainTreeStatus.UNKNOWN -> 1f
    }

/** Les arbres cueillis ou inaccessibles ne sont plus actionnables et passent donc en gris. */
fun DomainTreeStatus.isInactive() = this == DomainTreeStatus.PICKED_CLEAN || this == DomainTreeStatus.UNAVAILABLE

/** `@Composable` parce que la palette suit le theme via `MaterialTheme.fruxColors`. */
@Composable
fun HarvestPart?.markerTint(): Color = markerTint(MaterialTheme.fruxColors)

/** Surcharge pure qui garde la correspondance partie/palette testable sans harnais Compose. */
internal fun HarvestPart?.markerTint(colors: FruxExtendedColors): Color =
    when (this) {
        HarvestPart.FRUIT -> colors.markerFruit

        HarvestPart.FLEUR -> colors.markerFleur

        HarvestPart.FEUILLE -> colors.markerFeuille

        HarvestPart.NOIX -> colors.markerNoix

        HarvestPart.RACINE -> colors.markerRacine

        // Sans partie connue, aucune teinte de recolte ne peut etre affirmee.
        null -> colors.markerInactive
    }

/**
 * Résolution du statut affiché : voir [fr.frux.shared.presentation.resolveTreeStatus] (mapping
 * factorisé côté :shared, #87 — chaque feature en portait sa propre copie avant).
 *
 * Depuis #196, la couleur du marqueur suit ce statut résolu (via `.color()`) : la contrainte
 * produit qui figeait la couleur sur le statut domaine brut est levée. `markerAlpha()` ne fait
 * plus qu'une atténuation neutre des statuts non actionnables, et `markerTint()` (palette par
 * partie récoltée) ne sert plus que de repli visuel côté overlay ou reste simplement inutilisé.
 */
@OptIn(ExperimentalTime::class)
fun Tree.resolvedStatus(
    harvests: List<HarvestWindow>,
    currentMonth: Int,
    now: Instant,
): FruxStatus = resolveTreeStatus(status, harvests, currentMonth, lastObservedAt, now)

/**
 * Libellé de saison compact : "Jusqu'à fin <mois>" si une fenêtre est en cours (la plus éloignée
 * de sa fin si plusieurs se chevauchent), sinon la période de la prochaine fenêtre à venir (la
 * plus proche en mois, wrap compris). Null si aucune fenêtre. Même règle que
 * `TreeDetailMapper.seasonLabel`, dupliquée localement pour la même raison que [resolvedStatus].
 */
fun mapSeasonLabel(
    harvests: List<HarvestWindow>,
    currentMonth: Int,
): String? {
    if (harvests.isEmpty()) return null

    val ongoing = harvests.filter { it.isInSeason(currentMonth) }
    if (ongoing.isNotEmpty()) {
        val furthest = ongoing.maxBy { monthsForwardWrap(currentMonth, it.monthEnd) }
        return untilEndOfLabel(furthest.monthEnd)
    }

    val next = harvests.minBy { monthsForwardWrap(currentMonth, it.monthStart) }
    return harvestPeriodLabel(next.monthStart, next.monthEnd)
}

/** Nombre de mois (0..11) pour aller de [from] à [to] en avançant, avec wrap décembre→janvier. */
private fun monthsForwardWrap(
    from: Int,
    to: Int,
): Int = ((to - from) + 12) % 12

/**
 * Clé de glyphe d'un arbre pour le marqueur de carte. Délègue à la table unique du design system
 * (`SpeciesGlyphResolver.kt`), la même que celle des vignettes de liste : une seule correspondance
 * espèce → glyphe à maintenir (#87).
 */
fun speciesGlyphKey(
    scientificName: String,
    harvestPart: HarvestPart?,
): String? = speciesGlyphKey(scientificName, harvestPart?.name)

/** `@Composable` parce que la palette suit le theme via `MaterialTheme.fruxColors`. */
@Composable
fun speciesFamilyColor(
    scientificName: String,
    harvestPart: HarvestPart?,
): Color = speciesFamilyColor(scientificName, harvestPart, MaterialTheme.fruxColors)

/**
 * Surcharge pure, testable sans harnais Compose (meme pattern que [markerTint]).
 *
 * `harvestPart` ne pese sur aucune branche de [speciesFamilyKey] a ce jour (la famille suit le
 * genre, pas la partie recoltee — ex. le sureau reste Prunes&baies en fleur comme en fruit) ; il
 * reste dans la signature pour la symetrie avec `markerTint()`/`resolvedStatus()`, si une famille
 * future devait un jour se distinguer par partie.
 */
internal fun speciesFamilyColor(
    scientificName: String,
    @Suppress("UNUSED_PARAMETER") harvestPart: HarvestPart?,
    colors: FruxExtendedColors,
): Color = speciesFamilyKey(scientificName).legendColor(colors)

/**
 * Libellé français d'une famille produit, pour la légende de la carte (#133).
 *
 * `COQUE` n'est délibérément pas « Fruits à coque » : cette locution nomme déjà le filtre par
 * partie récoltée (`HARVEST_PART_CHIPS`, `HarvestPart.NOIX`) dans la barre de filtres — un même
 * libellé pour deux concepts différents (famille produit vs partie récoltée) aurait été ambigu à
 * l'écran, pas seulement en test.
 */
@Composable
internal fun SpeciesFamily.legendLabel(): String =
    when (this) {
        SpeciesFamily.POMME_POIRE -> stringResource(Res.string.map_legend_family_pomme_poire)
        SpeciesFamily.ROUGE -> stringResource(Res.string.map_legend_family_rouge)
        SpeciesFamily.DORE -> stringResource(Res.string.map_legend_family_dore)
        SpeciesFamily.PRUNE_BAIES -> stringResource(Res.string.map_legend_family_prune_baies)
        SpeciesFamily.COQUE -> stringResource(Res.string.map_legend_family_coque)
        SpeciesFamily.AUTRE -> stringResource(Res.string.map_legend_family_autre)
    }

/** Vrai seulement en phase « bientôt mûr » (#204) : porte le bord ambre du marqueur. */
internal fun FruxStatus.markerSoon(): Boolean = this == FruxStatus.Flowering

/**
 * Traduit les arbres du domaine en marqueurs de carte, via [TreeMarkerFacts] comme les arbres
 * isoles de la pyramide.
 *
 * `@Composable` parce que la couleur suit le theme (`MaterialTheme.fruxColors`).
 */
@OptIn(ExperimentalTime::class)
@Composable
fun List<Tree>.toMapMarkers(
    selectedId: Long?,
    speciesHarvests: Map<Long, List<HarvestWindow>>,
    currentMonth: Int,
    now: Instant,
    lockedSiteIds: Set<Long> = emptySet(),
): List<MapMarker> {
    val colors = MaterialTheme.fruxColors
    return map { tree ->
        val facts = tree.markerFacts(lockedSiteIds)
        facts.toMapMarker(
            colors = colors,
            harvests = speciesHarvests[tree.species.id] ?: emptyList(),
            currentMonth = currentMonth,
            now = now,
            label = facts.markerLabel(),
            selected = tree.id == selectedId,
        )
    }
}
